import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { createOnboardingRequest, getDemoConfig } from '@/services/api'

const schema = z.object({
  client_name: z.string()
    .min(3, 'Le nom du client doit contenir au moins 3 caractères')
    .regex(/^[a-z0-9-]+$/, 'Seuls les lettres minuscules, chiffres et tirets sont autorisés'),
  contact_email: z.string().email('Adresse email invalide'),
  docker_pat: z.string().optional(),
  github_pat: z.string().optional(),
  api_key: z.string().optional(),
  mtls_key_password: z.string().optional(),
  sig_key_password: z.string().optional(),
  code_membre: z.string()
    .min(3, 'Le code membre doit contenir au moins 3 caractères')
    .max(20, 'Maximum 20 caractères')
    .regex(/^[A-Z0-9]+$/, 'Majuscules et chiffres uniquement (e.g., TGB024)'),
  nom_participant: z.string()
    .min(3, 'Le nom du participant doit contenir au moins 3 caractères')
    .max(200, 'Maximum 200 caractères'),
  pays: z.string()
    .length(2, 'Code pays ISO sur 2 lettres (e.g., TG, CI)')
    .regex(/^[A-Z]{2}$/, 'Majuscules uniquement'),
  type_participant: z.string().max(10).optional(),
  ville: z.string().max(100).optional(),
  quartier: z.string().max(100).optional(),
  participant_api_url: z.string().max(500).optional(),
})

type FormData = z.infer<typeof schema>

export default function Onboarding() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [files, setFiles] = useState<{
    mtls_cert?: File
    mtls_chain?: File
    mtls_key?: File
    sig_cert?: File
    sig_chain?: File
    sig_key?: File
  }>({})

  const [demoMode, setDemoMode] = useState(false)

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    getDemoConfig()
      .then((cfg) => {
        if (cfg.enabled) {
          setDemoMode(true)
          Object.entries(cfg.prefill).forEach(([k, v]) => {
            setValue(k as keyof FormData, v as never)
          })
        }
      })
      .catch(() => {
        // demo endpoint missing or backend down: silently keep normal mode
      })
  }, [setValue])

  const fileLabels: Record<string, string> = {
    mtls_cert: 'certificat mTLS',
    mtls_chain: 'chaîne mTLS',
    mtls_key: 'clé privée mTLS',
    sig_cert: 'certificat de signature',
    sig_chain: 'chaîne de signature',
    sig_key: 'clé privée de signature',
  }

  const onSubmit = async (data: FormData) => {
    if (!demoMode) {
      const requiredFiles = ['mtls_cert', 'mtls_chain', 'mtls_key', 'sig_cert', 'sig_chain', 'sig_key'] as const
      for (const key of requiredFiles) {
        if (!files[key]) {
          toast({
            title: 'Fichier manquant',
            description: `Veuillez télécharger le ${fileLabels[key]}`,
            variant: 'destructive',
          })
          return
        }
      }
      if (!data.docker_pat || data.docker_pat.length < 10) {
        toast({ title: 'Docker PAT requis', variant: 'destructive' })
        return
      }
      if (!data.github_pat || data.github_pat.length < 10) {
        toast({ title: 'GitHub PAT requis', variant: 'destructive' })
        return
      }
    }

    setIsSubmitting(true)
    try {
      const formData = new FormData()
      Object.entries(data).forEach(([key, value]) => {
        if (value) formData.append(key, value)
      })
      if (demoMode) {
        formData.append('use_demo_secrets', 'true')
      } else {
        Object.entries(files).forEach(([key, file]) => {
          if (file) formData.append(key, file)
        })
      }

      const response = await createOnboardingRequest(formData)

      toast({
        title: 'Demande soumise avec succès !',
        description: `ID de votre demande : ${response.id}`,
      })

      navigate(`/status/${response.id}`)
    } catch {
      toast({
        title: 'Erreur',
        description: 'Échec de la soumission de la demande',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileChange = (key: keyof typeof files) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFiles(prev => ({ ...prev, [key]: file }))
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 bg-white py-8 px-6 lg:px-12 overflow-y-auto">
        <div className="max-w-xl mx-auto">
          {/* Back link */}
          <Link to="/" className="text-gold hover:text-gold/80 text-sm font-medium mb-6 inline-block">
            &larr; Retour à l'accueil
          </Link>

          <h1 className="text-2xl lg:text-3xl font-bold text-bronze mb-2">
            Demande d'Onboarding
          </h1>
          <p className="text-muted-foreground mb-8">
            Remplissez le formulaire pour soumettre votre demande d'intégration
          </p>

          {demoMode && (
            <div className="mb-6 rounded border border-gold bg-gold/10 px-4 py-3 text-sm text-bronze">
              Mode DEMO activé.
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Client Info */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-bronze border-b border-border pb-2">
                Informations Client
              </h2>

              <div>
                <Label htmlFor="client_name" className="text-foreground">Nom du Client *</Label>
                <Input
                  id="client_name"
                  placeholder="sandbox-tgb0xx"
                  className="mt-1 border-input focus:ring-gold focus:border-gold"
                  {...register('client_name')}
                />
                {errors.client_name && (
                  <p className="text-sm text-destructive mt-1">{errors.client_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="contact_email" className="text-foreground">Email de Contact *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  placeholder="admin@exemple.com"
                  className="mt-1 border-input focus:ring-gold focus:border-gold"
                  {...register('contact_email')}
                />
                {errors.contact_email && (
                  <p className="text-sm text-destructive mt-1">{errors.contact_email.message}</p>
                )}
              </div>
            </div>

            {/* Participant Info */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-bronze border-b border-border pb-2">
                Informations Participant
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code_membre" className="text-foreground">Code Membre BCEAO *</Label>
                  <Input
                    id="code_membre"
                    placeholder="TGB024"
                    className="mt-1 border-input focus:ring-gold focus:border-gold"
                    {...register('code_membre')}
                  />
                  {errors.code_membre && (
                    <p className="text-sm text-destructive mt-1">{errors.code_membre.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="pays" className="text-foreground">Pays (ISO) *</Label>
                  <Input
                    id="pays"
                    placeholder="TG"
                    maxLength={2}
                    className="mt-1 border-input focus:ring-gold focus:border-gold"
                    {...register('pays')}
                  />
                  {errors.pays && (
                    <p className="text-sm text-destructive mt-1">{errors.pays.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="nom_participant" className="text-foreground">Nom du Participant *</Label>
                <Input
                  id="nom_participant"
                  placeholder="DEMO PARTICIPANT TGB024"
                  className="mt-1 border-input focus:ring-gold focus:border-gold"
                  {...register('nom_participant')}
                />
                {errors.nom_participant && (
                  <p className="text-sm text-destructive mt-1">{errors.nom_participant.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="type_participant" className="text-foreground">Type</Label>
                  <Input
                    id="type_participant"
                    placeholder="B"
                    maxLength={10}
                    className="mt-1 border-input focus:ring-gold focus:border-gold"
                    {...register('type_participant')}
                  />
                  {errors.type_participant && (
                    <p className="text-sm text-destructive mt-1">{errors.type_participant.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="ville" className="text-foreground">Ville</Label>
                  <Input
                    id="ville"
                    placeholder="LOME"
                    className="mt-1 border-input focus:ring-gold focus:border-gold"
                    {...register('ville')}
                  />
                  {errors.ville && (
                    <p className="text-sm text-destructive mt-1">{errors.ville.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="quartier" className="text-foreground">Quartier</Label>
                  <Input
                    id="quartier"
                    placeholder="(par défaut = ville)"
                    className="mt-1 border-input focus:ring-gold focus:border-gold"
                    {...register('quartier')}
                  />
                  {errors.quartier && (
                    <p className="text-sm text-destructive mt-1">{errors.quartier.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="participant_api_url" className="text-foreground">URL API Participant</Label>
                <Input
                  id="participant_api_url"
                  placeholder="https://mock-backend.<client>.internal:8443 (auto-généré si vide)"
                  className="mt-1 border-input focus:ring-gold focus:border-gold"
                  {...register('participant_api_url')}
                />
                {errors.participant_api_url && (
                  <p className="text-sm text-destructive mt-1">{errors.participant_api_url.message}</p>
                )}
              </div>
            </div>

            {!demoMode && (<>
            {/* Credentials */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-bronze border-b border-border pb-2">
                Identifiants
              </h2>

              <div>
                <Label htmlFor="docker_pat">Docker PAT *</Label>
                <Input
                  id="docker_pat"
                  type="password"
                  className="mt-1"
                  {...register('docker_pat')}
                />
                {errors.docker_pat && (
                  <p className="text-sm text-destructive mt-1">{errors.docker_pat.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="github_pat">GitHub PAT *</Label>
                <Input
                  id="github_pat"
                  type="password"
                  className="mt-1"
                  {...register('github_pat')}
                />
                {errors.github_pat && (
                  <p className="text-sm text-destructive mt-1">{errors.github_pat.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="api_key">Clé API (optionnel)</Label>
                <Input
                  id="api_key"
                  type="password"
                  placeholder="Générée automatiquement si vide"
                  className="mt-1"
                  {...register('api_key')}
                />
              </div>
            </div>

            {/* mTLS Certificates */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-bronze border-b border-border pb-2">
                Certificats mTLS
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="mtls_cert">Certificat *</Label>
                  <Input
                    id="mtls_cert"
                    type="file"
                    accept=".pem,.crt"
                    className="mt-1"
                    onChange={handleFileChange('mtls_cert')}
                  />
                </div>
                <div>
                  <Label htmlFor="mtls_chain">Chaîne *</Label>
                  <Input
                    id="mtls_chain"
                    type="file"
                    accept=".pem,.crt"
                    className="mt-1"
                    onChange={handleFileChange('mtls_chain')}
                  />
                </div>
                <div>
                  <Label htmlFor="mtls_key">Clé Privée *</Label>
                  <Input
                    id="mtls_key"
                    type="file"
                    accept=".pem,.key"
                    className="mt-1"
                    onChange={handleFileChange('mtls_key')}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="mtls_key_password">Mot de passe de la clé (si chiffrée)</Label>
                <Input
                  id="mtls_key_password"
                  type="password"
                  className="mt-1"
                  {...register('mtls_key_password')}
                />
              </div>
            </div>

            {/* Signature Certificates */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-bronze border-b border-border pb-2">
                Certificats de Signature
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="sig_cert">Certificat *</Label>
                  <Input
                    id="sig_cert"
                    type="file"
                    accept=".pem,.crt"
                    className="mt-1"
                    onChange={handleFileChange('sig_cert')}
                  />
                </div>
                <div>
                  <Label htmlFor="sig_chain">Chaîne *</Label>
                  <Input
                    id="sig_chain"
                    type="file"
                    accept=".pem,.crt"
                    className="mt-1"
                    onChange={handleFileChange('sig_chain')}
                  />
                </div>
                <div>
                  <Label htmlFor="sig_key">Clé Privée *</Label>
                  <Input
                    id="sig_key"
                    type="file"
                    accept=".pem,.key"
                    className="mt-1"
                    onChange={handleFileChange('sig_key')}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="sig_key_password">Mot de passe de la clé (si chiffrée)</Label>
                <Input
                  id="sig_key_password"
                  type="password"
                  className="mt-1"
                  {...register('sig_key_password')}
                />
              </div>
            </div>
            </>)}

            <Button
              type="submit"
              className="w-full bg-gold hover:bg-gold/90 text-white font-semibold rounded-full py-6"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Envoi en cours...' : 'Soumettre la Demande'}
            </Button>
          </form>
        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-pattern-curves items-start justify-center p-12 pt-24">
        <div className="relative z-10 text-white max-w-md">
          <h2 className="text-3xl font-bold mb-6">
            Processus d'Intégration
          </h2>
          <p className="text-white/80 mb-8">
            Suivez les étapes pour intégrer votre système à la plateforme de Paiement Instantané.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-gold flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <div>
                <h3 className="font-semibold mb-1">Informations</h3>
                <p className="text-white/70 text-sm">Renseignez vos informations client et identifiants</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-gold flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <div>
                <h3 className="font-semibold mb-1">Certificats</h3>
                <p className="text-white/70 text-sm">Téléchargez vos certificats mTLS et de signature</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <div>
                <h3 className="font-semibold mb-1">Validation</h3>
                <p className="text-white/70 text-sm">Nos opérateurs valident votre demande</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <div>
                <h3 className="font-semibold mb-1">Provisionnement</h3>
                <p className="text-white/70 text-sm">Votre environnement est créé automatiquement</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
